import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './components/Login';
import HomeRedirect from './components/HomeRedirect';
import MainLayout from './components/MainLayout';
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import Doctors from './pages/SuperAdmin/Doctors';
import CompanySellers from './pages/SuperAdmin/CompanySellers';
import Settings from './pages/SuperAdmin/Settings';
import SuperAdminReports from './pages/SuperAdmin/Reports';
import ReklamSliders from './pages/SuperAdmin/ReklamSliders';
import Hospitals from './pages/SuperAdmin/Hospitals';
import MedicalSellerDashboard from './pages/MedicalSeller/Dashboard';
import MedicalSellerDoctors from './pages/MedicalSeller/Doctors';
import DoctorDetails from './pages/MedicalSeller/DoctorDetails';
import CompanySellerDashboard from './pages/CompanySeller/Dashboard';
import CompanySellerDoctors from './pages/CompanySeller/Doctors';
import CompanySellerDoctorDetails from './pages/CompanySeller/DoctorDetails';
import DoctorDashboard from './pages/Doctor/Dashboard';
import Patients from './pages/Doctor/Patients';
import PatientDetail from './pages/Doctor/PatientDetail';
import Secretaries from './pages/Doctor/Secretaries';
import Drugs from './pages/Doctor/Drugs';
import DataBackup from './pages/Doctor/DataBackup';
import LegacyRecords from './pages/Doctor/LegacyRecords';
import DoctorSettings from './pages/Doctor/Settings';
import Analytics from './pages/Doctor/Analytics';
import Accounting from './pages/Doctor/Accounting';
import TestsAttachments from './pages/Doctor/TestsAttachments';
import SickLeave from './pages/Doctor/SickLeave';
import Surgery from './pages/Doctor/Surgery';
import MedicalReport from './pages/Doctor/MedicalReport';
import AppointmentsReport from './pages/Doctor/AppointmentsReport';
import Appointments from './pages/Secretary/Appointments';
import Profile from './pages/Profile';
import AIDiagnosis from './pages/Doctor/AIDiagnosis';
import HospitalAdminDashboard from './pages/HospitalAdmin/Dashboard';
import Branches from './pages/HospitalAdmin/Branches';
import BranchStaff from './pages/HospitalAdmin/BranchStaff';
import DoctorReports from './pages/HospitalAdmin/DoctorReports';
import LabTests from './pages/Lab/Tests';
import LabCatalog from './pages/Lab/Catalog';
import PharmacyPrescriptions from './pages/Pharmacy/Prescriptions';
import OpticianOrders from './pages/Optician/Orders';
import Dental from './pages/Doctor/Dental';
import './i18n';
import './App.css';
// Last, so that it has the final word on a phone. See the note at its head.
import './responsive.css';

function AppContent() {
    const { i18n } = useTranslation();
    const [direction, setDirection] = useState(() => {
        const savedLang = localStorage.getItem('language') || 'en';
        return savedLang === 'ar' || savedLang === 'ku' ? 'rtl' : 'ltr';
    });

    useEffect(() => {
        const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
        setDirection(isRTL ? 'rtl' : 'ltr');
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [i18n.language]);

    return (
        <ConfigProvider
            direction={direction}
            theme={{
                token: {
                    colorPrimary: '#028090',
                    colorSuccess: '#02C39A',
                    colorInfo: '#00A896',
                    colorWarning: '#F0F3BD',
                    colorLink: '#00A896',
                    borderRadius: 8,
                    fontSize: 14,
                },
                components: {
                    Button: {
                        borderRadius: 8,
                        controlHeight: 40,
                        fontWeight: 500,
                    },
                    Card: {
                        borderRadiusLG: 12,
                        boxShadowTertiary: '0 2px 8px rgba(2, 128, 144, 0.08)',
                    },
                    Input: {
                        borderRadius: 8,
                        controlHeight: 40,
                    },
                    Table: {
                        borderRadius: 8,
                        headerBg: '#05668D',
                        headerColor: '#ffffff',
                    },
                },
            }}
        >
            <AuthProvider>
                <Router>
                    <Routes>
                        {/* Someone already signed in has no use for the form:
                            they go to their own first screen. */}
                        <Route path="/login" element={<HomeRedirect><Login /></HomeRedirect>} />

                        {/* Super Admin Routes */}
                        <Route path="/superadmin" element={
                            <PrivateRoute roles={['superadmin']}>
                                <MainLayout role="superadmin" />
                            </PrivateRoute>
                        }>
                            <Route path="dashboard" element={<SuperAdminDashboard />} />
                            <Route path="doctors" element={<Doctors />} />
                            <Route path="hospitals" element={<Hospitals />} />
                            <Route path="companysellers" element={<CompanySellers />} />
                            <Route path="reports" element={<SuperAdminReports />} />
                            <Route path="reklam-sliders" element={<ReklamSliders />} />
                            <Route path="settings" element={<Settings />} />                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Hospital Admin Routes */}
                        <Route path="/hospitaladmin" element={
                            <PrivateRoute roles={['hospitaladmin']}>
                                <MainLayout role="hospitaladmin" />
                            </PrivateRoute>
                        }>
                            <Route index element={<Navigate to="dashboard" replace />} />
                            <Route path="dashboard" element={<HospitalAdminDashboard />} />
                            <Route path="branches" element={<Branches />} />
                            <Route path="branch/:id" element={<BranchStaff />} />
                            <Route path="reports" element={<DoctorReports />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Lab Routes (branch lab) */}
                        <Route path="/lab" element={
                            <PrivateRoute roles={['lab']}>
                                <MainLayout role="lab" />
                            </PrivateRoute>
                        }>
                            <Route index element={<Navigate to="tests" replace />} />
                            <Route path="tests" element={<LabTests />} />
                            <Route path="catalog" element={<LabCatalog />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Pharmacy Routes (branch pharmacy) */}
                        <Route path="/pharmacy" element={
                            <PrivateRoute roles={['pharmacy']}>
                                <MainLayout role="pharmacy" />
                            </PrivateRoute>
                        }>
                            <Route index element={<Navigate to="prescriptions" replace />} />
                            <Route path="prescriptions" element={<PharmacyPrescriptions />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Optician Routes (glasses orders from the eye doctors) */}
                        <Route path="/optician" element={
                            <PrivateRoute roles={['optician']}>
                                <MainLayout role="optician" />
                            </PrivateRoute>
                        }>
                            <Route index element={<Navigate to="orders" replace />} />
                            <Route path="orders" element={<OpticianOrders />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Doctor Routes */}
                        <Route path="/doctor" element={
                            <PrivateRoute roles={['doctor']}>
                                <MainLayout role="doctor" />
                            </PrivateRoute>
                        }>
                            <Route path="dashboard" element={<DoctorDashboard />} />
                            <Route path="appointments" element={<Appointments />} />
                            <Route path="appointments-report" element={<AppointmentsReport />} />
                            <Route path="patients" element={<Patients />} />
                            <Route path="patient/:id" element={<PatientDetail />} />
                            <Route path="patient/:patientId/tests" element={<TestsAttachments />} />
                            <Route path="secretaries" element={<Secretaries />} />
                            <Route path="drugs" element={<Drugs />} />
                            <Route path="analytics" element={<Analytics />} />
                            <Route path="accounting" element={<Accounting />} />
                            <Route path="sick-leave" element={<SickLeave />} />
                            <Route path="surgery" element={<Surgery />} />
                            <Route path="dental" element={<Dental />} />
                            <Route path="medical-report" element={<MedicalReport />} />
                            <Route path="data" element={<DataBackup />} />
                            <Route path="legacy-records" element={<LegacyRecords />} />
                            <Route path="ai-diagnosis" element={<AIDiagnosis />} />
                            <Route path="settings" element={<DoctorSettings />} />
                            <Route path="profile" element={<Profile />} />

                        </Route>

                        {/* Medical Seller Routes */}
                        <Route path="/medicalseller" element={
                            <PrivateRoute roles={['medicalseller']}>
                                <MainLayout role="medicalseller" />
                            </PrivateRoute>
                        }>
                            <Route path="dashboard" element={<MedicalSellerDashboard />} />
                            <Route path="doctors" element={<MedicalSellerDoctors />} />
                            <Route path="doctor/:id" element={<DoctorDetails />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Company Seller Routes */}
                        <Route path="/companyseller" element={
                            <PrivateRoute roles={['companyseller']}>
                                <MainLayout role="companyseller" />
                            </PrivateRoute>
                        }>
                            <Route path="dashboard" element={<CompanySellerDashboard />} />
                            <Route path="doctors" element={<CompanySellerDoctors />} />
                            <Route path="doctor/:id" element={<CompanySellerDoctorDetails />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Secretary Routes */}
                        <Route path="/secretary" element={
                            <PrivateRoute roles={['secretary']}>
                                <MainLayout role="secretary" />
                            </PrivateRoute>
                        }>
                            <Route index element={<Navigate to="appointments" replace />} />
                            <Route path="appointments" element={<Appointments />} />                            <Route path="appointments-report" element={<AppointmentsReport />} />                            <Route path="patients" element={<Patients />} />
                            <Route path="patient/:id" element={<PatientDetail />} />
                            <Route path="dental" element={<Dental />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        <Route path="/" element={<HomeRedirect />} />
                    </Routes>
                </Router>
            </AuthProvider>
        </ConfigProvider>
    );
}

function App() {
    return <AppContent />;
}

export default App;
